import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaStar, FaQuoteLeft, FaQuoteRight, FaArrowLeft, FaArrowRight, FaCircle } from 'react-icons/fa';

const testimonials = [
  {
    id: 1,
    name: 'Esther Weru',
    position: 'Director, OnTime Lending Company Ltd.',
    text: 'PowerFinancials has transformed our lending process through automation and robust functionality, improving efficiency, accuracy and customer service.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Esther+Weru&background=E8960D&color=fff&size=64',
    color: 'from-amber-500 to-orange-500',
    company: 'OnTime Lending Company Ltd.',
    location: 'Kenya'
  },
  {
    id: 2,
    name: 'Florence Ndeto',
    position: 'Director, Spin Capital Ltd.',
    text: 'Micropoint provided us with a solution tailored to our needs, giving us better visibility of our business and helping us serve our customers more effectively.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Florence+Ndeto&background=074578&color=fff&size=64',
    color: 'from-blue-500 to-indigo-500',
    company: 'Spin Capital Ltd.',
    location: 'Kenya'
  },
  {
    id: 3,
    name: 'Geoffrey N. Mark',
    position: 'Manager, Umowa SACCO Society Ltd.',
    text: 'Micropoint successfully implemented PowerFinancials for our SACCO, including requirements gathering, data migration, user training and commissioning. We strongly recommend their professional and quality service.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Geoffrey+N+Mark&background=0a7e3a&color=fff&size=64',
    color: 'from-emerald-500 to-teal-500',
    company: 'Umowa SACCO Society Ltd.',
    location: 'Kenya'
  },
  {
    id: 4,
    name: 'Shadrack Osanay',
    position: 'Treasurer, Iron SACCO Society Ltd.',
    text: 'Micropoint successfully implemented PowerFinancials for our SACCO, including data migration, training and commissioning. We strongly recommend Micropoint for their professional and quality work.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Shadrack+Osanay&background=7c3aed&color=fff&size=64',
    color: 'from-purple-500 to-pink-500',
    company: 'Iron SACCO Society Ltd.',
    location: 'Kenya'
  },
  {
    id: 5,
    name: 'Eng. Mung\'eria Kimani',
    position: 'Managing Director, KIRI Consult Limited',
    text: 'We worked with Micropoint Systems as a software consultant in the development of monitoring software for Kenya Roads Board. We recommend Micropoint for software development engagements.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Mungeria+Kimani&background=dc2626&color=fff&size=64',
    color: 'from-red-500 to-rose-500',
    company: 'KIRI Consult Limited',
    location: 'Kenya'
  },
  {
    id: 6,
    name: 'Kenya Roads Board',
    position: 'Government / Enterprise Client',
    text: 'Micropoint delivered consultancy and development services for a fully functional Contracts Database, including implementation and final reporting.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Kenya+Roads+Board&background=0891b2&color=fff&size=64',
    color: 'from-cyan-500 to-blue-500',
    company: 'Kenya Roads Board',
    location: 'Kenya'
  },
  {
    id: 7,
    name: 'Patrick Langat',
    position: 'Director, Zasa Academy',
    text: 'Micropoint provided exceptional school management solutions that streamlined our administrative processes and improved parent-teacher communication.',
    rating: 5,
    image: 'https://ui-avatars.com/api/?name=Patrick+Langat&background=059669&color=fff&size=64',
    color: 'from-emerald-500 to-green-500',
    company: 'Zasa Academy',
    location: 'Kenya'
  }
];

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(3);

  // Handle responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex + itemsPerView >= testimonials.length ? 0 : prevIndex + itemsPerView
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, itemsPerView]);

  const totalSlides = Math.ceil(testimonials.length / itemsPerView);
  const maxIndex = Math.max(0, totalSlides - 1);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex + itemsPerView >= testimonials.length ? 0 : prevIndex + itemsPerView
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex - itemsPerView < 0 ? Math.max(0, testimonials.length - itemsPerView) : prevIndex - itemsPerView
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index * itemsPerView);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const getVisibleTestimonials = () => {
    return testimonials.slice(currentIndex, currentIndex + itemsPerView);
  };

  return (
    <section className="section bg-white border-y border-gray-200 scroll-mt-20">
      <div className="container-custom">
        <div className="text-center mb-10 md:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-3">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-display">
              What Our <span className="text-micropoint-gold">Clients Say</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-micropoint-gold to-yellow-500 mx-auto mt-4 rounded-full" />
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">
              Trusted by organizations across financial services, education, healthcare and enterprise
            </p>
          </motion.div>
        </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
              >
                {getVisibleTestimonials().map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className="group relative bg-white rounded-2xl border border-gray-200 hover:border-micropoint-gold hover:shadow-xl transition-all duration-300 overflow-hidden"
                  >
                    {/* Top gradient bar */}
                    <div className={`h-1 w-full bg-gradient-to-r ${testimonial.color}`} />
                    
                    {/* Quote icon */}
                    <div className="absolute top-4 right-4 opacity-10">
                      <FaQuoteRight className="text-4xl text-gray-800" />
                    </div>
                    
                    <div className="p-6">
                      {/* Header with avatar and name */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="relative">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name} 
                            className="w-14 h-14 rounded-full border-2 border-white shadow-md" 
                          />
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 group-hover:text-micropoint-gold transition-colors duration-300 text-sm md:text-base">
                            {testimonial.name}
                          </p>
                          <p className="text-xs text-gray-500">{testimonial.position}</p>
                          <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5">
                            <span>{testimonial.company}</span>
                            <span>•</span>
                            <span>{testimonial.location}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Rating stars */}
                      <div className="flex mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <FaStar 
                            key={i} 
                            className="text-yellow-400 group-hover:scale-110 transition-transform duration-300 text-sm" 
                          />
                        ))}
                      </div>
                      
                      {/* Testimonial text with quote marks */}
                      <div className="relative">
                        <FaQuoteLeft className="absolute -top-1 -left-1 text-micropoint-gold/10 text-xl" />
                        <p className="text-gray-600 text-sm leading-relaxed pl-4 group-hover:text-gray-800 transition-colors duration-300 line-clamp-3">
                          {testimonial.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          {testimonials.length > itemsPerView && (
            <>
              <button
                onClick={prevSlide}
                className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white text-gray-700 hover:text-micropoint-gold w-8 h-8 md:w-10 md:h-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-micropoint-gold flex items-center justify-center z-10"
                aria-label="Previous testimonial"
              >
                <FaArrowLeft className="text-xs md:text-sm" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white text-gray-700 hover:text-micropoint-gold w-8 h-8 md:w-10 md:h-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-micropoint-gold flex items-center justify-center z-10"
                aria-label="Next testimonial"
              >
                <FaArrowRight className="text-xs md:text-sm" />
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        {totalSlides > 1 && (
          <div className="flex justify-center gap-2 mt-6 md:mt-8">
            {Array.from({ length: totalSlides }).map((_, index) => {
              const isActive = Math.floor(currentIndex / itemsPerView) === index;
              return (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    isActive 
                      ? 'w-8 h-2.5 bg-micropoint-gold shadow-lg shadow-micropoint-gold/20' 
                      : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              );
            })}
          </div>
        )}

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-gray-200"
        >
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
            <span className="text-sm text-gray-500 font-medium">Trusted by organizations across:</span>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {['SACCOs', 'Banks', 'Microfinance', 'NGOs', 'Government', 'Healthcare', 'Education'].map((org, i) => (
                <span key={i} className="text-xs text-gray-500 bg-gray-50 px-3 py-1 rounded-full border border-gray-200 hover:border-micropoint-gold hover:text-micropoint-gold transition-all">
                  {org}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}